import { useState, useEffect } from 'react';
import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon, ChevronRightIcon, PlusIcon, TrashIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type {
  CollectionCreateInput,
  CollectionUpdateInput,
  CollectionColumn,
  CollectionWithDetails,
  ResourceOperation,
} from '@/types/entities';

interface FormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CollectionCreateInput | CollectionUpdateInput) => Promise<void>;
  onCancel?: () => void;
  collection?: CollectionWithDetails;
  title?: string;
  description?: string;
}

const COLUMN_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'integer', label: 'Integer' },
  { value: 'double', label: 'Number' },
  { value: 'datetime', label: 'Date/Time' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'image', label: 'Image' },
  { value: 'richtext', label: 'Rich Text' },
  { value: 'array', label: 'Array' },
] as const;

const ARRAY_ITEM_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'datetime', label: 'Date/Time' },
  { value: 'image', label: 'Media (Asset ID)' },
  { value: 'richtext', label: 'Rich Text' },
] as const;

const ALL_OPERATIONS: ResourceOperation[] = ['create', 'update', 'delete'];

interface ArrayFieldFormData {
  name: string;
  label: string;
  type: 'text' | 'datetime' | 'image' | 'richtext';
  required: boolean;
}

interface ColumnFormData {
  name: string;
  label: string;
  type: string;
  required: boolean;
  default: string;
  items?: {
    fields: ArrayFieldFormData[];
  };
}

export function Form({
  open,
  onOpenChange,
  onSubmit,
  onCancel,
  collection,
  title = 'Collection',
  description = 'Define the schema for this collection.',
}: FormProps) {
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [columns, setColumns] = useState<ColumnFormData[]>([]);
  const [ops, setOps] = useState<ResourceOperation[]>(ALL_OPERATIONS);
  const [collapsedColumnIndexes, setCollapsedColumnIndexes] = useState<Set<number>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!collection;

  useEffect(() => {
    if (collection) {
      setName(collection.name);
      setLabel(collection.label ?? '');
      setColumns(
        collection.details.columns.map((col) => ({
          name: col.name,
          label: col.label,
          type: col.type,
          required: col.required || false,
          default: String(col.default ?? ''),
          items:
            col.type === 'array'
              ? {
                  fields: (col.items?.fields ?? []).map((field) => ({
                    name: field.name,
                    label: field.label,
                    type: field.type,
                    required: field.required || false,
                  })),
                }
              : undefined,
        }))
      );
      setCollapsedColumnIndexes(new Set(collection.details.columns.map((_, index) => index)));
      const configuredOps = collection.details.ops?.length ? collection.details.ops : ALL_OPERATIONS;
      const normalizedOps = Array.from(new Set<ResourceOperation>(configuredOps));
      setOps(ALL_OPERATIONS.filter((operation) => normalizedOps.includes(operation)));
    } else {
      setName('');
      setLabel('');
      setColumns([]);
      setCollapsedColumnIndexes(new Set());
      setOps(ALL_OPERATIONS);
    }
    setError(null);
  }, [collection]);

  const toggleOperation = (operation: ResourceOperation, checked: boolean) => {
    setOps((currentOps) => {
      const next = checked
        ? [...currentOps, operation]
        : currentOps.filter((value) => value !== operation);

      const normalized = Array.from(new Set<ResourceOperation>(next));
      return ALL_OPERATIONS.filter((value) => normalized.includes(value));
    });
  };

  const addColumn = () => {
    setColumns((current) => {
      const nextColumns = [
        ...current,
        { name: '', label: '', type: 'text', required: false, default: '', items: undefined },
      ];
      const newIndex = nextColumns.length - 1;
      setCollapsedColumnIndexes((currentCollapsed) => {
        const nextCollapsed = new Set(currentCollapsed);
        nextCollapsed.delete(newIndex);
        return nextCollapsed;
      });
      return nextColumns;
    });
  };

  const removeColumn = (index: number) => {
    setColumns((current) => {
      const nextColumns = current.filter((_, i) => i !== index);
      setCollapsedColumnIndexes((currentCollapsed) => {
        const nextCollapsed = new Set<number>();

        currentCollapsed.forEach((collapsedIndex) => {
          if (collapsedIndex < index) {
            nextCollapsed.add(collapsedIndex);
            return;
          }

          if (collapsedIndex > index) {
            nextCollapsed.add(collapsedIndex - 1);
          }
        });

        return nextCollapsed;
      });
      return nextColumns;
    });
  };

  const moveColumn = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= columns.length) {
      return;
    }

    const nextColumns = [...columns];
    const [movedColumn] = nextColumns.splice(index, 1);
    nextColumns.splice(targetIndex, 0, movedColumn);
    setColumns(nextColumns);
    setCollapsedColumnIndexes((currentCollapsed) => {
      const nextCollapsed = new Set(currentCollapsed);
      const sourceCollapsed = currentCollapsed.has(index);
      const targetCollapsed = currentCollapsed.has(targetIndex);

      if (sourceCollapsed) {
        nextCollapsed.add(targetIndex);
      } else {
        nextCollapsed.delete(targetIndex);
      }

      if (targetCollapsed) {
        nextCollapsed.add(index);
      } else {
        nextCollapsed.delete(index);
      }

      return nextCollapsed;
    });
  };

  const toggleColumnCollapsed = (index: number) => {
    setCollapsedColumnIndexes((currentCollapsed) => {
      const nextCollapsed = new Set(currentCollapsed);
      if (nextCollapsed.has(index)) {
        nextCollapsed.delete(index);
      } else {
        nextCollapsed.add(index);
      }
      return nextCollapsed;
    });
  };

  const updateColumn = (index: number, updates: Partial<ColumnFormData>) => {
    setColumns(
      columns.map((col, i) => (i === index ? { ...col, ...updates } : col))
    );
  };

  const addArrayField = (columnIndex: number) => {
    setColumns(
      columns.map((column, index) => {
        if (index !== columnIndex) {
          return column;
        }

        return {
          ...column,
          items: {
            fields: [
              ...(column.items?.fields ?? []),
              { name: '', label: '', type: 'text', required: false },
            ],
          },
        };
      })
    );
  };

  const removeArrayField = (columnIndex: number, fieldIndex: number) => {
    setColumns(
      columns.map((column, index) => {
        if (index !== columnIndex) {
          return column;
        }

        return {
          ...column,
          items: {
            fields: (column.items?.fields ?? []).filter((_, index) => index !== fieldIndex),
          },
        };
      })
    );
  };

  const updateArrayField = (
    columnIndex: number,
    fieldIndex: number,
    updates: Partial<ArrayFieldFormData>
  ) => {
    setColumns(
      columns.map((column, index) => {
        if (index !== columnIndex) {
          return column;
        }

        return {
          ...column,
          items: {
            fields: (column.items?.fields ?? []).map((field, index) =>
              index === fieldIndex ? { ...field, ...updates } : field
            ),
          },
        };
      })
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Collection name is required');
      return;
    }
    if (!/^[a-z0-9_]+$/i.test(name)) {
      setError('Collection name must contain only letters, numbers, and underscores');
      return;
    }
    if (columns.length === 0) {
      setError('At least one column is required');
      return;
    }

    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      if (!col.name.trim()) {
        setError(`Column ${i + 1}: Name is required`);
        return;
      }
      if (!/^[a-z0-9_]+$/i.test(col.name)) {
        setError(`Column ${i + 1}: Name must contain only letters, numbers, and underscores`);
        return;
      }
      if (!col.label.trim()) {
        setError(`Column ${i + 1}: Label is required`);
        return;
      }
      const duplicates = columns.filter((c, j) => c.name.trim() === col.name.trim() && j !== i);
      if (duplicates.length > 0) {
        setError(`Column "${col.name}": Duplicate column name`);
        return;
      }

      if (col.type === 'array') {
        const arrayFields = col.items?.fields ?? [];
        if (arrayFields.length === 0) {
          setError(`Column "${col.name}": Add at least one array item field`);
          return;
        }

        const names = new Set<string>();
        for (let fieldIndex = 0; fieldIndex < arrayFields.length; fieldIndex++) {
          const field = arrayFields[fieldIndex];
          if (!field.name.trim()) {
            setError(`Column "${col.name}" item field ${fieldIndex + 1}: Name is required`);
            return;
          }
          if (!/^[a-z0-9_]+$/i.test(field.name)) {
            setError(`Column "${col.name}" item field ${fieldIndex + 1}: Name must contain only letters, numbers, and underscores`);
            return;
          }
          if (!field.label.trim()) {
            setError(`Column "${col.name}" item field ${fieldIndex + 1}: Label is required`);
            return;
          }
          if (names.has(field.name.trim())) {
            setError(`Column "${col.name}" item field "${field.name}": Duplicate field name`);
            return;
          }
          names.add(field.name.trim());
        }
      }
    }

    setIsSubmitting(true);
    try {
      const collectionColumns: CollectionColumn[] = columns.map((col) => {
        const column: CollectionColumn = {
          name: col.name.trim(),
          label: col.label.trim(),
          type: col.type,
        };
        if (col.required) column.required = true;
        if (col.type === 'array') {
          column.items = {
            fields: (col.items?.fields ?? []).map((field) => ({
              name: field.name.trim(),
              label: field.label.trim(),
              type: field.type,
              ...(field.required ? { required: true } : {}),
            })),
          };
          return column;
        }
        if (col.default.trim()) {
          if (col.type === 'integer') {
            column.default = parseInt(col.default, 10);
          } else if (col.type === 'double') {
            column.default = parseFloat(col.default);
          } else if (col.type === 'boolean') {
            column.default = col.default.toLowerCase() === 'true';
          } else {
            column.default = col.default;
          }
        }
        return column;
      });

      const data: CollectionCreateInput | CollectionUpdateInput = {
        name: name.trim(),
        label: label.trim() || undefined,
        details: {
          columns: collectionColumns,
          ops: ALL_OPERATIONS.filter((operation) => ops.includes(operation)),
        },
      };
      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit' : 'Create'} {title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="name">Collection Name</Label>
              <Input
                id="name"
                placeholder="blog_posts"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Letters, numbers, and underscores only
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="label">Display Label</Label>
              <Input
                id="label"
                placeholder="Blog Posts"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Human-readable name for UI (optional)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Allowed Operations</Label>
              <div className="grid grid-cols-3 gap-2 rounded-lg border p-3">
                {ALL_OPERATIONS.map((operation) => {
                  return (
                    <div key={operation} className="flex items-center gap-2">
                      <Checkbox
                        id={`collection-op-${operation}`}
                        checked={ops.includes(operation)}
                        onCheckedChange={(checked) => toggleOperation(operation, !!checked)}
                        disabled={isSubmitting}
                      />
                      <Label htmlFor={`collection-op-${operation}`} className="text-sm capitalize">
                        {operation}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Columns</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addColumn}
                  disabled={isSubmitting}
                  className="gap-1"
                >
                  <PlusIcon className="size-3" />
                  Add Column
                </Button>
              </div>

              {columns.length === 0 ? (
                <div className="border border-dashed rounded-lg p-8 text-center text-muted-foreground">
                  No columns defined. Click "Add Column" to get started.
                </div>
              ) : (
                <div className="space-y-2">
                  {columns.map((column, index) => (
                    <Collapsible
                      key={index}
                      open={!collapsedColumnIndexes.has(index)}
                      onOpenChange={() => toggleColumnCollapsed(index)}
                      className="border rounded-lg p-2 bg-muted/20"
                    >
                      <div className="flex items-center gap-2">
                        <CollapsibleTrigger asChild>
                          <Button type="button" variant="ghost" className="h-auto flex-1 justify-start px-2 py-1 text-left">
                            {collapsedColumnIndexes.has(index) ? (
                              <ChevronRightIcon className="mr-2 size-4 text-muted-foreground" />
                            ) : (
                              <ChevronDownIcon className="mr-2 size-4 text-muted-foreground" />
                            )}
                            <span className="truncate">
                              {column.label.trim() || 'Untitled field'}
                            </span>
                          </Button>
                        </CollapsibleTrigger>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => moveColumn(index, -1)}
                          disabled={isSubmitting || index === 0}
                        >
                          <ArrowUpIcon className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => moveColumn(index, 1)}
                          disabled={isSubmitting || index === columns.length - 1}
                        >
                          <ArrowDownIcon className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeColumn(index)}
                          disabled={isSubmitting}
                        >
                          <TrashIcon className="size-4 text-destructive" />
                        </Button>
                      </div>

                      <CollapsibleContent className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            placeholder="column_name"
                            value={column.name}
                            onChange={(e) => updateColumn(index, { name: e.target.value })}
                            disabled={isSubmitting}
                            className="flex-1"
                          />
                          <Input
                            placeholder="Label"
                            value={column.label}
                            onChange={(e) => updateColumn(index, { label: e.target.value })}
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Select
                            value={column.type}
                            onValueChange={(value) =>
                              updateColumn(index, {
                                type: value,
                                items: value === 'array'
                                  ? { fields: column.items?.fields ?? [] }
                                  : undefined,
                              })
                            }
                            disabled={isSubmitting}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                              {COLUMN_TYPES.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`required-${index}`}
                              checked={column.required}
                              onCheckedChange={(checked) =>
                                updateColumn(index, { required: !!checked })
                              }
                              disabled={isSubmitting}
                            />
                            <Label htmlFor={`required-${index}`} className="text-sm">
                              Required
                            </Label>
                          </div>
                          {column.type !== 'array' && (
                            <Input
                              placeholder="Default value"
                              value={column.default}
                              onChange={(e) => updateColumn(index, { default: e.target.value })}
                              disabled={isSubmitting}
                              className="flex-1"
                            />
                          )}
                        </div>
                        {column.type === 'array' && (
                          <div className="space-y-2 rounded-md border border-dashed p-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Array Item Fields</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addArrayField(index)}
                              disabled={isSubmitting}
                              className="gap-1"
                            >
                              <PlusIcon className="size-3" />
                              Add Field
                            </Button>
                          </div>

                          {(column.items?.fields ?? []).length === 0 ? (
                            <div className="rounded border border-dashed p-3 text-xs text-muted-foreground">
                              Define at least one field for each array row.
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {(column.items?.fields ?? []).map((field, fieldIndex) => (
                                <div key={fieldIndex} className="rounded border bg-background p-2">
                                  <div className="mb-2 flex items-center gap-2">
                                    <Input
                                      placeholder="field_name"
                                      value={field.name}
                                      onChange={(e) =>
                                        updateArrayField(index, fieldIndex, { name: e.target.value })
                                      }
                                      disabled={isSubmitting}
                                      className="flex-1"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => removeArrayField(index, fieldIndex)}
                                      disabled={isSubmitting}
                                    >
                                      <TrashIcon className="size-4 text-destructive" />
                                    </Button>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <Input
                                      placeholder="Label"
                                      value={field.label}
                                      onChange={(e) =>
                                        updateArrayField(index, fieldIndex, { label: e.target.value })
                                      }
                                      disabled={isSubmitting}
                                    />
                                    <Select
                                      value={field.type}
                                      onValueChange={(value: 'text' | 'datetime' | 'image' | 'richtext') =>
                                        updateArrayField(index, fieldIndex, { type: value })
                                      }
                                      disabled={isSubmitting}
                                    >
                                      <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Type" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {ARRAY_ITEM_TYPES.map((type) => (
                                          <SelectItem key={type.value} value={type.value}>
                                            {type.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="mt-2 flex items-center gap-2">
                                    <Checkbox
                                      id={`array-required-${index}-${fieldIndex}`}
                                      checked={field.required}
                                      onCheckedChange={(checked) =>
                                        updateArrayField(index, fieldIndex, { required: !!checked })
                                      }
                                      disabled={isSubmitting}
                                    />
                                    <Label htmlFor={`array-required-${index}-${fieldIndex}`} className="text-sm">
                                      Required in each row
                                    </Label>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                onCancel?.();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
